/**
 * Media Library Types
 *
 * Defines all TypeScript interfaces and types used throughout the Media Library component.
 * These types are provider-agnostic and work with any asset provider.
 */

import { IAsset } from '../providers';

/**
 * Abstract media types supported by the library
 */
export enum MediaType {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  Model3D = '3d-model',
  Video360 = '360-video',
  Image360 = '360-image',
  HDRI = 'hdri',
  Texture = 'texture',
  Font = 'font',
  Avatar = 'avatar',
  Unknown = 'unknown',
}

/**
 * Media file status
 */
export type MediaFileStatus = 'pending' | 'ready' | 'error';

/**
 * Fixed library sources (UI/routing concepts that map to Zapworks provider)
 */
export type LibrarySource =
  | 'user-library'      // User's personal uploads
  | 'workspace-library' // Workspace shared assets
  | 'trash'             // Trash bin
  | 'zappar-library';   // Curated Zappar assets (read-only)

/**
 * Asset source identifier
 * Can be a fixed library source or any provider ID from the registry
 */
export type AssetSource = LibrarySource | string;

/**
 * Media type card for the selection view
 */
export interface MediaTypeCard {
  type: MediaType;
  title: string;
  description: string;
  previewImage?: string;
  isEnabled: boolean;
}

/**
 * Sort options for asset lists
 */
export type SortOption = 'date-asc' | 'date-desc' | 'name-asc' | 'name-desc' | 'file-size-asc' | 'file-size-desc';
export type SortDirection = 'asc' | 'desc';

/**
 * Filters options for asset lists
 */
export interface Filters {
  templateType: string[];
  status: string[];
  labels: string[];
  animated: string[];
  categories?: string[];
}

/**
 * Operation mode of the MediaLibrary component
 */
export type MediaLibraryMode = 'tool' | 'cms';

/**
 * Media Library view state
*/
export type MediaLibraryView = 'selection' | 'browser' | 'home' | 'explore' | 'asset' | 'gltf-missing-files' | 'analytics';

/** Theme options for the Media Library */
export type MediaLibraryTheme = 'plain' | 'nebula' | 'teal' | 'teal-dark';

/**
 * Media browser state
 */
export interface MediaBrowserState {
  currentMediaType: MediaType;
  activeSource: AssetSource;
  searchQuery: string;
  sortBy: SortOption;
  selectedAssets: string[];
  selectedAssetId?: string;
  loading: boolean;
  error?: string;
}

/**
 * Callback when an asset is selected
 */
export type AssetSelectCallback = (asset: IAsset) => void;

export interface AssetRowState {
  assets: IAsset[];
  count: number;
  loading: boolean;
  error?: string;
}

export type TemplateTypes = 'standard' | '3d-viewer' | 'configurator';

export const TEMPLATE_TYPE_OPTIONS: { label: string; value: TemplateTypes }[] = [
  { label: 'Standard', value: 'standard' },
  { label: '3D Viewer', value: '3d-viewer' },
  { label: 'Configurator', value: 'configurator' },
];
